// frontend/src/pages/admin/ProductForm.js
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, Select, Checkbox, Row, Col, Space, Popconfirm, Table, Divider, InputNumber, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, MailOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';

const { TextArea } = Input;
const { Option } = Select;

const ProductForm = ({ visible, onCancel, onSuccess, product, productCategories: propProductCategories, defaultCategoryCode }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false); // For main form submission
  const [allProductCategories, setAllProductCategories] = useState([]);

  // Loading states for dropdowns
  const [loadingUsersForAssignment, setLoadingUsersForAssignment] = useState(false);
  const [allUsersForAssignment, setAllUsersForAssignment] = useState([]);
  const [loadingRolesForAssignment, setLoadingRolesForAssignment] = useState(false);
  const [allRolesForAssignment, setAllRolesForAssignment] = useState([]);
  const [loadingOrganizationsForAssignment, setLoadingOrganizationsForAssignment] = useState(false);
  const [allOrganizationsForAssignment, setAllOrganizationsForAssignment] = useState([]);
  const [loadingEmailGroups, setLoadingEmailGroups] = useState(false);
  const [emailGroups, setEmailGroups] = useState([]);

  // States for assignment inputs
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [userToAssign, setUserToAssign] = useState(null);
  const [roleToAssign, setRoleToAssign] = useState(null);
  const [organizationToAssign, setOrganizationToAssign] = useState(null);

  const [assigningLoading, setAssigningLoading] = useState(false); // For all assignment actions (buttons and table)

  const notificationApi = useNotificationApi();

  // Helper function to show errors (persistent notification, top-right)
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  // Helper function to show success (transient notification, top-right)
  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  // Helper function to show loading (sticky notification, top-right, with delay and min display time)
  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Fetch all available product categories for the dropdown (from prop if provided, else fetch)
  const fetchAllProductCategories = useCallback(async () => {
    if (propProductCategories && propProductCategories.length > 0) {
      setAllProductCategories(propProductCategories);
      return;
    }
    const loadingKey = 'fetchProductCategoriesFormLoading';
    const hideLoading = showLoading(loadingKey, 'Loading product categories...');
    try {
      // Using the dedicated available-product-categories endpoint
      const response = await api.get('/api/products/available-product-categories');
      setAllProductCategories(response.data.data || []);
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error fetching all product categories:', error.response?.data || error.message);
      showError('Failed to Load Categories', error.response?.data?.message || 'Failed to load product categories for the form.', 'fetchProductCategoriesFormError');
    }
  }, [propProductCategories, showLoading, showError]);

  // Fetch all available users for individual assignment dropdown
  const fetchAvailableUsersForAssignment = useCallback(async () => {
    setLoadingUsersForAssignment(true);
    const loadingKey = 'fetchUsersForAssignmentLoading';
    const hideLoading = showLoading(loadingKey, 'Loading users for assignment...');
    try {
      // --- FIXED: Using the dedicated available-users endpoint ---
      const response = await api.get('/api/products/available-users');
      setAllUsersForAssignment(response.data.data || []); // Data is already mapped to {id, name}
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error fetching available users for assignment:', error.response?.data || error.message);
      showError('Failed to Load Users', error.response?.data?.message || 'Failed to load users for assignment.', 'fetchUsersForAssignmentError');
    } finally {
      setLoadingUsersForAssignment(false);
    }
  }, [showLoading, showError]);

  // Fetch all available roles for bulk assignment by role dropdown
  const fetchAvailableRolesForAssignment = useCallback(async () => {
    setLoadingRolesForAssignment(true);
    const loadingKey = 'fetchRolesForAssignmentLoading';
    const hideLoading = showLoading(loadingKey, 'Loading roles for assignment...');
    try {
      // --- FIXED: Using the dedicated available-roles endpoint ---
      const response = await api.get('/api/products/available-roles');
      setAllRolesForAssignment(response.data.data || []); // Data is already mapped to {id, name}
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error fetching available roles for assignment:', error.response?.data || error.message);
      showError('Failed to Load Roles', error.response?.data?.message || 'Failed to load roles for assignment.', 'fetchRolesForAssignmentError');
    } finally {
      setLoadingRolesForAssignment(false);
    }
  }, [showLoading, showError]);

  // Fetch all available organizations for bulk assignment by organization dropdown
  const fetchAvailableOrganizationsForAssignment = useCallback(async () => {
    setLoadingOrganizationsForAssignment(true);
    const loadingKey = 'fetchOrgsForAssignmentLoading';
    const hideLoading = showLoading(loadingKey, 'Loading organizations for assignment...');
    try {
      // --- FIXED: Using the dedicated available-organizations endpoint ---
      const response = await api.get('/api/products/available-organizations');
      setAllOrganizationsForAssignment(response.data.data || []); // Data is already mapped to {id, name}
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error fetching available organizations for assignment:', error.response?.data || error.message);
      showError('Failed to Load Organizations', error.response?.data?.message || 'Failed to load organizations for assignment.', 'fetchOrgsForAssignmentError');
    } finally {
      setLoadingOrganizationsForAssignment(false);
    }
  }, [showLoading, showError]);

  // Fetch all available email groups for the notification dropdown
  const fetchEmailGroups = useCallback(async () => {
    setLoadingEmailGroups(true);
    const loadingKey = 'fetchEmailGroupsLoading';
    const hideLoading = showLoading(loadingKey, 'Loading email groups...');
    try {
      const response = await api.get('/api/email-groups', { params: { activeOnly: true } });
      setEmailGroups(response.data.data || []);
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error fetching email groups for product form:', error.response?.data || error.message);
      showError('Failed to Load Email Groups', error.response?.data?.message || 'Failed to load email groups for selection.', 'fetchEmailGroupsError');
    } finally {
      setLoadingEmailGroups(false);
    }
  }, [showLoading, showError]);

  // Refetch assigned users for the current product (after individual or bulk assignment)
  const refetchAssignedUsers = useCallback(async (currentProductId = product?.id) => {
    if (!currentProductId) {
      console.warn('refetchAssignedUsers called without a valid product ID. Cannot fetch assigned users.');
      setAssignedUsers([]);
      return;
    }
    setAssigningLoading(true);
    const loadingKey = 'refetchAssignedUsersLoading';
    const hideLoading = showLoading(loadingKey, 'Refreshing assigned users...');
    try {
      const response = await api.get(`/api/products/${currentProductId}`);
      setAssignedUsers(response.data?.data?.assignedUsers || []);
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error refetching assigned users:', error.response?.data || error.message);
      showError('Failed to Refresh Assignments', error.response?.data?.message || 'Failed to refresh assigned users list.', 'refetchAssignedUsersError');
    } finally {
      setAssigningLoading(false);
    }
  }, [product?.id, showLoading, showError]);

  // Effect to populate form fields and fetch initial data when modal opens or 'product' prop changes
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setAssignedUsers([]);
      setUserToAssign(null);
      setRoleToAssign(null);
      setOrganizationToAssign(null);
      // Reset fetched dropdown data as well to ensure fresh fetch on next open
      setEmailGroups([]);
      setAllProductCategories([]);
      setAllUsersForAssignment([]);
      setAllRolesForAssignment([]);
      setAllOrganizationsForAssignment([]);
      return;
    }

    // Fetch all necessary dropdown data when the modal becomes visible
    fetchAllProductCategories();
    fetchAvailableUsersForAssignment();
    fetchAvailableRolesForAssignment();
    fetchAvailableOrganizationsForAssignment();
    fetchEmailGroups();

    if (product) {
      form.setFieldsValue({
        name: product.name,
        code: product.code,
        description: product.description,
        productCategoryId: product.productCategoryId,
        isActive: Boolean(product.isActive),
        notificationEmailGroupId: product.notificationEmailGroupId || undefined,
        sortOrder: product.sortOrder !== undefined && product.sortOrder !== null ? product.sortOrder : 0,
      });
      refetchAssignedUsers(product.id);
    } else {
      form.resetFields();
      setAssignedUsers([]);
      setUserToAssign(null);
      setRoleToAssign(null);
      setOrganizationToAssign(null);

      // 🆕 Client Computing Portal — pre-select category based on tab
      // Handled after categories are loaded (see separate effect below)
      form.setFieldsValue({
        isActive: true,
        sortOrder: 0,
      });
    }
  }, [visible, product, form, fetchAllProductCategories, fetchAvailableUsersForAssignment,
      fetchAvailableRolesForAssignment, fetchAvailableOrganizationsForAssignment, fetchEmailGroups, refetchAssignedUsers]);

  // 🆕 Client Computing Portal — auto-select category based on defaultCategoryCode
  // Only applies in CREATE mode (not edit), and only when categories are loaded
  useEffect(() => {
    // Only in create mode
    if (product) return;
    if (!visible) return;
    if (!allProductCategories || allProductCategories.length === 0) return;
    if (!defaultCategoryCode) return;

    // Skip if user already picked a category (avoid overriding)
    const currentValue = form.getFieldValue('productCategoryId');
    if (currentValue) return;

    let targetCategory = null;

    if (defaultCategoryCode === 'EUC') {
      targetCategory = allProductCategories.find(c => c.code === 'EUC');
    } else if (defaultCategoryCode === '!EUC') {
      targetCategory = allProductCategories.find(c => c.code !== 'EUC');
    } else {
      targetCategory = allProductCategories.find(c => c.code === defaultCategoryCode);
    }

    if (targetCategory) {
      form.setFieldsValue({ productCategoryId: targetCategory.id });
    }
  }, [visible, product, allProductCategories, defaultCategoryCode, form]);

  // Handle main form submission
  const handleSubmit = async () => {
    let hideLoading;
    try {
      setLoading(true);
      const loadingKey = 'productSubmitLoading';
      hideLoading = showLoading(loadingKey, product ? 'Updating product...' : 'Creating product...');

      const values = await form.validateFields();

      const payload = {
        name: values.name,
        code: values.code,
        description: values.description || null,
        productCategoryId: Number(values.productCategoryId),
        isActive: values.isActive,
        notificationEmailGroupId: values.notificationEmailGroupId || null,
        sortOrder: values.sortOrder !== undefined && values.sortOrder !== null ? values.sortOrder : 0,
      };

      let response;
      if (product) {
        response = await api.put(`/api/products/${product.id}`, payload);
        showSuccess('Product Updated', 'Product details updated successfully!', 'productUpdateSuccess');
      } else {
        response = await api.post('/api/products', payload);
        showSuccess('Product Created', 'New product created successfully!', 'productCreateSuccess');
      }

      if (hideLoading && typeof hideLoading === 'function') hideLoading();
      onSuccess(true);
      onCancel();


    } catch (error) {
      if (hideLoading && typeof hideLoading === 'function') hideLoading();
      setLoading(false);
      console.error('ProductForm: Form submission error:', error.response?.data || error.message || error);
      showError('Submission Failed', error.response?.data?.message || error.message || 'Failed to save product due to an unexpected error.', 'productFormSubmitError');
    } finally {
      setLoading(false);
    }
  };

  // --- Functions for Assigning/Removing Users (Individual) ---

  const handleAssignUser = async () => {
    if (!userToAssign) {
      showError('Selection Required', 'Please select a user to assign.', 'assignUserWarning');
      return;
    }
    if (!product || !product.id) {
        showError('Action Not Allowed', 'Cannot assign user to a non-existent product. Please save the product first.', 'assignUserNoProductError');
        return;
    }

    setAssigningLoading(true);
    const loadingKey = `assignUserLoading-${userToAssign}`;
    const hideLoading = showLoading(loadingKey, 'Assigning user...');
    try {
      const response = await api.post(`/api/products/${product.id}/users`, { userId: userToAssign });
      hideLoading();
      showSuccess('User Assigned', response.data.message || 'User assigned successfully!', 'assignUserSuccess');
      setUserToAssign(null);
      await refetchAssignedUsers();
    } catch (error) {
      hideLoading();
      console.error('Error assigning user:', error.response?.data || error.message);
      showError('Assignment Failed', error.response?.data?.message || 'Failed to assign user.', `assignUserError-${userToAssign}`);
    } finally {
      setAssigningLoading(false);
    }
  };

  const handleRemoveAssignedUser = async (userId, userName) => {
    if (!product || !product.id) {
        showError('Action Not Allowed', 'Cannot remove user from a non-existent product.', 'removeUserNoProductError');
        return;
    }
    setAssigningLoading(true);
    const loadingKey = `removeUserLoading-${userId}`;
    const hideLoading = showLoading(loadingKey, `Removing ${userName}...`);
    try {
      const response = await api.patch(`/api/products/${product.id}/users/${userId}/remove`);
      hideLoading();
      showSuccess('User Removed', response.data.message || 'User removed successfully!', 'removeUserSuccess');
      await refetchAssignedUsers();
    } catch (error) {
      hideLoading();
      console.error('Error removing user:', error.response?.data || error.message);
      showError('Removal Failed', error.response?.data?.message || 'Failed to remove user.', `removeUserError-${userId}`);
    } finally {
      setAssigningLoading(false);
    }
  };

  const assignedUsersColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title={`Are you sure you want to remove "${record.name}" from this product?`}
          onConfirm={() => handleRemoveAssignedUser(record.userId, record.name)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ loading: assigningLoading }}
        >
          <Button type="link" danger icon={<DeleteOutlined />} aria-label={`Remove ${record.name}`} />
        </Popconfirm>
      ),
    },
  ];

  // Filter out users that are already assigned from the 'Add User' dropdown
  const availableUsersForAssignment = allUsersForAssignment.filter(
    (user) => !assignedUsers.some((assigned) => assigned.userId === user.id)
  );

  // --- Functions for Bulk Assignment by Role ---
  const handleAssignByRole = async () => {
    if (!roleToAssign) {
      showError('Selection Required', 'Please select a role to assign.', 'assignRoleWarning');
      return;
    }
    if (!product || !product.id) {
      showError('Action Not Allowed', 'Cannot assign to role for a non-existent product. Please save the product first.', 'assignRoleNoProductError');
      return;
    }

    setAssigningLoading(true);
    const loadingKey = `assignRoleLoading-${roleToAssign}`;
    const hideLoading = showLoading(loadingKey, 'Assigning by role...');
    try {
      const response = await api.post(`/api/products/${product.id}/assign-by-role`, { roleId: roleToAssign });
      hideLoading();
      showSuccess('Assigned by Role', response.data.message || 'Product assigned to users by role successfully!', 'assignRoleSuccess');
      setRoleToAssign(null);
      await refetchAssignedUsers();
    } catch (error) {
      hideLoading();
      console.error('Error assigning product to role:', error.response?.data || error.message);
      showError('Assignment Failed', error.response?.data?.message || 'Failed to assign product to role.', `assignRoleError-${roleToAssign}`);
    } finally {
      setAssigningLoading(false);
    }
  };

  // --- Functions for Bulk Assignment by Organization ---
  const handleAssignByOrganization = async () => {
    if (!organizationToAssign) {
      showError('Selection Required', 'Please select an organization to assign.', 'assignOrgWarning');
      return;
    }
    if (!product || !product.id) {
      showError('Action Not Allowed', 'Cannot assign to organization for a non-existent product. Please save the product first.', 'assignOrgNoProductError');
      return;
    }

    setAssigningLoading(true);
    const loadingKey = `assignOrgLoading-${organizationToAssign}`;
    const hideLoading = showLoading(loadingKey, 'Assigning by organization...');
    try {
      const response = await api.post(`/api/products/${product.id}/assign-by-organization`, { organizationId: organizationToAssign });
      hideLoading();
      showSuccess('Assigned by Organization', response.data.message || 'Product assigned to users by organization successfully!', 'assignOrgSuccess');
      setOrganizationToAssign(null);
      await refetchAssignedUsers();
    } catch (error) {
      hideLoading();
      console.error('Error assigning product to organization:', error.response?.data || error.message);
      showError('Assignment Failed', error.response?.data?.message || 'Failed to assign product to organization.', `assignOrgError-${organizationToAssign}`);
    } finally {
      setAssigningLoading(false);
    }
  };


  return (
    <Modal
      open={visible}
      title={product ? 'Edit Product' : 'Add New Product'}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading || assigningLoading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={800}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        name="product_form"
        initialValues={{ isActive: true, sortOrder: 0 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Product Name"
              rules={[
                { required: true, message: 'Please input the product name!' },
                { type: 'string', min: 2, message: 'Product name must be at least 2 characters.' }
              ]}
            >
              <Input placeholder="e.g., OpenText Application Content Management" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="code"
              label="Product Code"
              rules={[
                { required: true, message: 'Please input the product code!' },
                { type: 'string', min: 2, max: 50, message: 'Product code must be between 2 and 50 characters.' }
              ]}
            >
              <Input placeholder="e.g., ACMv20" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Description"
        >
          <TextArea rows={3} placeholder="Brief description of the product" />
        </Form.Item>

        <Form.Item
          name="productCategoryId"
          label="Product Category"
          rules={[{ required: true, message: 'Please select a product category!' }]}
        >
          <Select
            placeholder="Select a product category"
            loading={!allProductCategories.length}
            showSearch
            filterOption={(input, option) =>
                option.children && option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {allProductCategories.map(category => (
              <Option key={category.id} value={category.id}>
                {category.name} ({category.code})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="notificationEmailGroupId"
          label="Product-Specific Notification Email Group"
          tooltip={{
            title: "Select an email group (e.g., DMS Support, SIC Support) to receive notifications for tickets related to this specific product. This email will be included in the 'To' field for new ticket notifications, alongside the default support group.",
            icon: <MailOutlined />
          }}
        >
          <Select
            placeholder="Select a notification group (optional)"
            allowClear
            loading={loadingEmailGroups}
            showSearch
            filterOption={(input, option) =>
              option.children && option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {emailGroups.map(group => (
              <Option key={group.id} value={group.id}>
                {group.name} ({group.group_email_address})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="sortOrder"
          label="Sort Order"
          tooltip="Determines the display order in lists. Lower numbers appear first."
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="isActive"
          label="Status"
          valuePropName="checked"
        >
          <Checkbox>Active</Checkbox>
        </Form.Item>


        {/* --- Assignment Sections (Only in Edit Mode) --- */}
        {product && (
          <>
            <Divider orientation="left">Product Assignments</Divider>

            {/* Assign by Individual User */}
            <div style={{ marginBottom: '30px', paddingBottom: '20px' }}>
              <h3>Assign Individual User</h3>
              <Row gutter={16} align="middle" style={{ marginBottom: '15px' }}>
                <Col span={16}>
                  <Select
                    showSearch
                    placeholder="Select user to assign (search by name or email)"
                    style={{ width: '100%' }}
                    value={userToAssign}
                    onChange={setUserToAssign}
                    loading={loadingUsersForAssignment}
                    disabled={loadingUsersForAssignment || availableUsersForAssignment.length === 0 || assigningLoading}
                    filterOption={(input, option) =>
                      option.children && option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {availableUsersForAssignment.map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={8}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAssignUser}
                    loading={assigningLoading}
                    disabled={!userToAssign || assigningLoading}
                  >
                    Assign User
                  </Button>
                </Col>
              </Row>
            </div>

            {/* Assign by Role */}
            <div style={{ marginBottom: '30px', paddingBottom: '20px' }}>
              <h3>Assign by Role</h3>
              <Row gutter={16} align="middle" style={{ marginBottom: '15px' }}>
                <Col span={16}>
                  <Select
                    showSearch
                    placeholder="Select role to assign product to all users in this role"
                    style={{ width: '100%' }}
                    value={roleToAssign}
                    onChange={setRoleToAssign}
                    loading={loadingRolesForAssignment}
                    disabled={loadingRolesForAssignment || !allRolesForAssignment.length || assigningLoading}
                    filterOption={(input, option) =>
                      option.children && option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {allRolesForAssignment.map(role => (
                      <Option key={role.id} value={role.id}>
                        {role.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={8}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAssignByRole}
                    loading={assigningLoading}
                    disabled={!roleToAssign || assigningLoading}
                  >
                    Assign Role
                  </Button>
                </Col>
              </Row>
            </div>

            {/* Assign by Organization */}
            <div style={{ marginBottom: '30px', paddingBottom: '20px' }}>
              <h3>Assign by Organization</h3>
              <Row gutter={16} align="middle" style={{ marginBottom: '15px' }}>
                <Col span={16}>
                  <Select
                    showSearch
                    placeholder="Select organization to assign product to all users in this organization"
                    style={{ width: '100%' }}
                    value={organizationToAssign}
                    onChange={setOrganizationToAssign}
                    loading={loadingOrganizationsForAssignment}
                    disabled={loadingOrganizationsForAssignment || !allOrganizationsForAssignment.length || assigningLoading}
                    filterOption={(input, option) =>
                      option.children && option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {allOrganizationsForAssignment.map(org => (
                      <Option key={org.id} value={org.id}>
                        {org.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col span={8}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAssignByOrganization}
                    loading={assigningLoading}
                    disabled={!organizationToAssign || assigningLoading}
                  >
                    Assign Org
                  </Button>
                </Col>
              </Row>
            </div>


            {/* Assigned Users Table - This remains the consolidated view */}
            <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
              <h3>Currently Assigned Users</h3>
              <Table
                columns={assignedUsersColumns}
                dataSource={assignedUsers.sort((a,b) => (a.name || '').localeCompare(b.name || ''))}
                rowKey="userId"
                pagination={false}
                loading={assigningLoading}
                size="small"
                locale={{ emptyText: 'No users assigned to this product yet.' }}
              />
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ProductForm;